"use client";

import { Link } from "@/navigation";
import { useTranslations } from "next-intl";
import { useModalInquiryContext } from "@/context/ModalInquiryContext";

export interface ColorCtaProps {
	// 翻译命名空间
	translationNamespace?: string;
	// 自定义标题（优先级高于翻译）
	title?: string;
	// 自定义按钮文字（优先级高于翻译）
	buttonText?: string;
	// 按钮跳转链接（buttonType 为 "link" 时生效）
	buttonHref?: string;
	// 翻译键
	titleKey?: string;
	buttonTextKey?: string;
	// 四角标签文案翻译键
	label1Key?: string;
	label2Key?: string;
	label3Key?: string;
	label4Key?: string;
	// 自定义容器类名
	className?: string;
	// 按钮类型：link 跳转 | modal 弹窗，默认跳转
	buttonType?: "link" | "modal";
}

export default function ColorCta({
	translationNamespace,
	title,
	buttonText,
	buttonHref = "/upload",
	titleKey = "ctaTitle",
	buttonTextKey = "ctaButton",
	label1Key = "ctaLabel1",
	label2Key = "ctaLabel2",
	label3Key = "ctaLabel3",
	label4Key = "ctaLabel4",
	className = "",
	buttonType = "link",
}: ColorCtaProps) {
	const t = translationNamespace ? useTranslations(translationNamespace) : null;
	const { openModalInquiry } = useModalInquiryContext();

	// 默认文案
	const defaultTexts = {
		title: "Start Your CNC Project Now?",
		button: "Get Instant CNC Quote",
		label1: "Multiple Machining Services",
		label2: "DFM Analysis",
		label3: "Quick Quote",
		label4: "Easy Delivery",
	};

	// 获取显示文案
	const displayTitle = title || (t ? t(titleKey) : defaultTexts.title);
	const displayButtonText = buttonText || (t ? t(buttonTextKey) : defaultTexts.button);

	// 四角标签文案
	const label1 = t ? t(label1Key) : defaultTexts.label1;
	const label2 = t ? t(label2Key) : defaultTexts.label2;
	const label3 = t ? t(label3Key) : defaultTexts.label3;
	const label4 = t ? t(label4Key) : defaultTexts.label4;

	// 按钮样式
	const buttonClassName =
		"inline-flex items-center justify-center rounded bg-themePrimary400 px-8 py-3 text-base font-semibold text-white  transition-all duration-300 hover:-translate-y-0.5 hover:bg-mainColor hover:text-white  sm:px-10 sm:py-4 ";

	return (
		<section className={`bg-white py-8 sm:py-10 md:py-12 lg:py-16 ${className}`}>
			<div className="container">
				{/* 外层容器 - 用于定位溢出的标签 */}
				<div className="relative mx-auto">
					{/* 浮动标签 - 左上（蓝色） - 溢出定位 */}
					<div className="absolute -left-3 top-14 z-20 hidden sm:block md:-left-4 md:top-16">
						<span className="inline-block rounded border border-blue-200 bg-blue-50 px-3 py-1.5 text-sm font-medium text-blue-600 shadow-sm">
							{label1}
						</span>
					</div>

					{/* 浮动标签 - 左下（红色） - 溢出定位 */}
					<div className="absolute -left-3 bottom-10 z-20 hidden sm:block md:-left-4 md:bottom-12">
						<span className="inline-block rounded border border-red-200 bg-red-50 px-3 py-1.5 text-sm font-medium text-red-500 shadow-sm">
							{label3}
						</span>
					</div>

					{/* 浮动标签 - 右上（红色） - 溢出定位 */}
					<div className="absolute -right-3 top-14 z-20 hidden sm:block md:-right-4 md:top-16">
						<span className="inline-block rounded border border-red-200 bg-red-50 px-3 py-1.5 text-sm font-medium text-red-500 shadow-sm">
							{label2}
						</span>
					</div>

					{/* 浮动标签 - 右下（绿色） - 溢出定位 */}
					<div className="absolute -right-3 bottom-10 z-20 hidden sm:block md:-right-4 md:bottom-12">
						<span className="inline-block rounded border border-green-200 bg-green-50 px-3 py-1.5 text-sm font-medium text-green-500 shadow-sm">
							{label4}
						</span>
					</div>

					{/* 浏览器窗口容器 */}
					<div className="relative overflow-hidden rounded-xl">
						{/* 浏览器顶部栏 - 三色圆点 */}
						<div className="flex h-8 items-center gap-2 bg-[#f4f4f4] px-4 sm:h-10">
							<div className="flex gap-1.5 sm:gap-2">
								<span className="h-2.5 w-2.5 rounded-full bg-[#ff5f57] sm:h-3 sm:w-3" />
								<span className="h-2.5 w-2.5 rounded-full bg-[#febc2e] sm:h-3 sm:w-3" />
								<span className="h-2.5 w-2.5 rounded-full bg-[#28c840] sm:h-3 sm:w-3" />
							</div>
						</div>

						{/* 内容区域 */}
						<div className="relative px-4 py-12 sm:px-8 sm:py-16 md:py-20 lg:py-24">
							{/* 左侧装饰横线组 - 上 */}
							<div className="absolute left-6 top-14 hidden flex-col gap-2 sm:left-10 md:flex lg:left-16">
								<div className="h-1.5 w-10 rounded-full bg-gray-300/50 lg:w-12" />
								<div className="h-1.5 w-16 rounded-full bg-gray-300/50 lg:w-20" />
								<div className="h-1.5 w-8 rounded-full bg-gray-300/50 lg:w-10" />
								<div className="h-1.5 w-14 rounded-full bg-gray-300/50 lg:w-16" />
							</div>

							{/* 左侧装饰横线组 - 下 */}
							<div className="absolute bottom-14 left-6 hidden flex-col gap-2 sm:left-10 md:flex lg:bottom-16 lg:left-16">
								<div className="h-1.5 w-14 rounded-full bg-gray-300/50 lg:w-16" />
								<div className="h-1.5 w-20 rounded-full bg-gray-300/50 lg:w-24" />
								<div className="h-1.5 w-10 rounded-full bg-gray-300/50 lg:w-12" />
								<div className="h-1.5 w-16 rounded-full bg-gray-300/50 lg:w-20" />
							</div>

							{/* 右侧装饰横线组 - 上 */}
							<div className="absolute right-6 top-14 hidden flex-col items-end gap-2 sm:right-10 md:flex lg:right-16">
								<div className="h-1.5 w-10 rounded-full bg-gray-300/50 lg:w-14" />
								<div className="h-1.5 w-16 rounded-full bg-gray-300/50 lg:w-20" />
								<div className="h-1.5 w-6 rounded-full bg-gray-300/50 lg:w-10" />
								<div className="h-1.5 w-12 rounded-full bg-gray-300/50 lg:w-16" />
							</div>

							{/* 右侧装饰横线组 - 下 */}
							<div className="absolute bottom-14 right-6 hidden flex-col items-end gap-2 sm:right-10 md:flex lg:bottom-16 lg:right-16">
								<div className="h-1.5 w-8 rounded-full bg-gray-300/50 lg:w-12" />
								<div className="lg:w-18 h-1.5 w-14 rounded-full bg-gray-300/50" />
								<div className="h-1.5 w-6 rounded-full bg-gray-300/50 lg:w-8" />
							</div>

							{/* 中间正方形边框组 */}
							<div className="pointer-events-none absolute inset-x-0 top-1/2 hidden -translate-y-1/2 items-end justify-center gap-3 md:flex lg:gap-4">
								<div className="h-12 w-12 rounded border border-gray-300/50 lg:h-14 lg:w-14" />
								<div className="h-14 w-14 rounded border border-gray-300/50 lg:h-16 lg:w-16" />
								<div className="h-12 w-12 rounded border border-gray-300/50 lg:h-14 lg:w-14" />
								<div className="h-16 w-16 rounded border border-gray-300/50 lg:h-[72px] lg:w-[72px]" />
							</div>

							{/* 中心内容 */}
							<div className="relative z-10 text-center">
								{/* 标题 */}
								<h2 className="mb-6 text-2xl font-bold leading-tight text-black sm:mb-8 sm:text-[30px] md:text-4xl">
									{displayTitle}
								</h2>

								{/* 按钮 */}
								{buttonType === "link" ? (
									<Link href={buttonHref} className={buttonClassName}>
										{displayButtonText}
									</Link>
								) : (
									<button onClick={openModalInquiry} className={buttonClassName}>
										{displayButtonText}
									</button>
								)}
							</div>
						</div>
					</div>
				</div>
			</div>
		</section>
	);
}
